import { useState, useRef, useEffect } from "react";
import { ChevronDownIcon } from "lucide-react";

const faqs = [
  {
    q: "What is the Sagar Mein Yog Learning Management System (LMS) and who can access it?",
    a: "The Sagar Mein Yog Learning Management System (LMS) is a digital e-learning platform designed to promote wellness and mindfulness among seafarers. It provides structured learning through video modules, quizzes, and assessments focused on physical, mental, and emotional wellbeing. Access to the LMS is available exclusively to registered seafarers who are part of the Sagar Mein Yog initiative.",
  },
  {
    q: "How many modules are available in the LMS and is it mandatory to complete them?",
    a: "The LMS consists of 43 carefully curated modules that together form the complete Sagar Mein Yog wellness curriculum. Each module contributes to different aspects of overall wellbeing. Completing all 43 modules is mandatory to ensure participants experience the full scope of the program and qualify for certification.",
  },
  {
    q: "How do I log in to the LMS and create my account?",
    a: "To create your account, visit the LMS portal and click on the 'Sign Up' button. Enter your INDoS Number and registered email ID, then create a password of your choice. You will receive a verification email, click on the verification link to activate your account. Once verified, you can log in using your email and password to begin your learning journey.",
  },
  {
    q: "What should I do if I forget my password or want to change it?",
    a: "If you forget your password or wish to reset it, click on the 'Forgot Password' link on the login page. Enter your registered email ID and select 'Send Reset Link.' You’ll receive an email containing a secure link to reset your password. Follow the instructions to create a new password and regain access to your LMS account.",
  },
  {
    q: "Can I access the LMS from my phone, tablet, or laptop, and do I need internet access?",
    a: "The LMS is currently optimized for desktop and laptop devices only. Accessing it from mobile phones or tablets may not provide the best experience, as certain features such as video playback, navigation, and assessments are designed for larger screens. You will also need a stable internet connection to stream videos, complete assessments, and ensure your progress is tracked accurately.",
  },  
  {
    q: "Will my progress be saved automatically if I log out?",
    a: "Yes, your progress is automatically saved in real-time as you watch videos or complete quizzes and assessments. This means you can log out anytime and resume exactly where you left off without losing your learning data.",
  },
  {
    q: "How are the modules organized in the LMS and do I have to complete them in a specific order?",
    a: "Modules are organized in a structured yet flexible manner across different pillars of wellness. While it’s recommended to complete them sequentially for a better learning flow, you can start with any module depending on your interest or current need. The LMS allows self-paced and flexible learning.",
  },
  {
    q: "Are there separate modules for Pre-Sea and Post-Sea training?",
    a: "Yes, the LMS provides separate sets of modules for Pre-Sea and Post-Sea seafarers. Each track is designed with relevant content tailored to the experiences, challenges, and wellbeing requirements of those stages in a seafarer’s career.",
  },
  {
    q: "How much time does it take to complete one module and can I revisit completed modules anytime?",
    a: "Each module varies in duration depending on its content—some may take 10–15 minutes, while others may be longer. You can revisit and rewatch any completed module anytime to refresh your learning or practice specific techniques again.",
  },
  {
    q: "Does each module include quizzes and assessments, and are they mandatory?",
    a: "Yes, most modules include short quizzes at the end to help reinforce key concepts. Each pillar also concludes with an assessment that evaluates your understanding. While quizzes are optional for practice, assessments are mandatory and contribute to your overall completion status.",
  },
  {
    q: "Are quizzes timed, and what is the passing score?",
    a: "Quizzes are designed for self-practice and are not timed, allowing you to complete them at your own pace. However, pillar assessments are timed to simulate real-world evaluation scenarios. To pass an assessment, you must score at least 75%.",
  },
  {
    q: "Can I retake the assessment if I don't pass, and how many attempts are allowed?",
    a: "Yes, you can retake any assessment if you do not achieve the passing score. The LMS allows unlimited reattempts, though the number of attempts may be configurable by administrators. Reattempting helps reinforce your understanding of key topics.",
  },
  {
    q: "Do I have to clear the assessment before moving to the next module?",
    a: "No, it’s not necessary to clear one assessment before moving to the next module. You have full flexibility to explore and learn at your own pace, and you can always return later to complete any pending assessments.",
  },
  {
    q: "Will I get feedback on my assessment performance?",
    a: "Yes, upon completing any assessment, the LMS provides an instant performance summary showing your total score, number of correct and incorrect answers, and the areas where you can improve. This helps you track your learning progress effectively.",
  },
  {
    q: "How can I track my learning progress in the LMS?",
    a: "Your progress across modules, quizzes, and assessments is automatically tracked within your user dashboard. You can view your completion percentage, pending modules, and assessment scores. For deeper insights, click on the 'Analysis' button to see detailed analytics of your performance.",
  },
  {
    q: "Will I receive a certificate after completing all modules, and how will I know I've finished the program?",
    a: "Yes, upon completing all modules within a specific pillar, you will receive a digital certificate recognizing your achievement in that pillar. Once you’ve successfully completed all pillars and earned every certificate, you will be officially recognized as having completed the entire Sagar Mein Yog program. Certificates can be downloaded directly from your LMS dashboard.",
  },
];


function AccordionItem({ faq, index, isOpen, onToggle }) {
  const contentRef = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      if (isOpen) {
        // Use requestAnimationFrame to ensure DOM is updated
        requestAnimationFrame(() => {
          if (contentRef.current) {
            setHeight(contentRef.current.scrollHeight);
          }
        });
      } else {
        setHeight(0);
      }
    }
  }, [isOpen]);

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 text-left text-md text-black font-medium flex items-start justify-between gap-4 hover:bg-gray-50 transition-colors duration-200 rounded-t-2xl"
      >
        <span className="flex-1">
          {index + 1}. {faq.q}
        </span>
        <ChevronDownIcon
          className={`text-gray-500 size-5 shrink-0 transition-transform duration-300 ease-out ${
            isOpen ? "rotate-180" : "rotate-0"
          }`}
        />
      </button>
      <div
        ref={contentRef}
        className="overflow-hidden transition-all duration-350 ease-out"
        style={{
          maxHeight: `${height}px`,
          opacity: isOpen ? 1 : 0,
          transitionProperty: "max-height, opacity",
          transitionDuration: "350ms",
          transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div className="px-6 pb-4 text-black/70 pt-0">
          {faq.a}
        </div>
      </div>
    </div>
  );
}

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState(null);

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="bg-gray-100">
      <div className="px-14">
        <div className="py-8 pt-0 flex items-center gap-6">
          <h1 className="text-4xl font-helvetica font-bold md:text-5xl text-[#296780]">
            FAQs
          </h1>
          <div className="flex-1 h-px bg-[#2B6A7F]" />
        </div>
        <p className="text-gray-600 mb-10 font-segoe text-md">
          Here are some common questions we receive, along with detailed answers
          to help you out. If your question isn't listed, feel free to reach out
          to our dedicated technical support team anytime for assistance!
        </p>
      </div>

      <div className="px-8 space-y-4 mx-auto">
        {faqs.map((faq, index) => (
          <AccordionItem
            key={index}
            faq={faq}
            index={index}
            isOpen={openIndex === index}
            onToggle={() => handleToggle(index)}
          />
        ))}
      </div>
    </section>
  );
}

